import {
  KeyboardEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Button, Col, Dropdown } from "react-bootstrap";
import { useQuery } from "react-query";

import LedgerBalancesTable from "../components/LedgerBalancesTable";
import PaginationComponent from "../components/Pagination";
import { USER_ROLES } from "../constants";
import { useAuth } from "../contexts/AuthContext";
import { useRegion } from "../contexts/RegionContext";
import { getLedgerBalances } from "../services/ledgerBalancesServices";
import {
  LedgerBalanceComparisonOp,
  LedgerBalanceCurrency,
  LedgerBalanceCursor,
  LedgerBalanceResult,
  LedgerBalanceSearchQuery,
  LedgerBalanceSortDirection,
  LedgerBalanceSortField,
} from "../types/ledgerBalances";

import "../components/Search/index.scss";
import styles from "../styles/ledgerBalances.module.scss";

const PAGE_SIZE = 25;

const LEDGER_COUNTRIES = ["AE", "HK", "SG"] as const;

type LedgerCountry = typeof LEDGER_COUNTRIES[number];

const COUNTRY_LABELS: Record<LedgerCountry, string> = {
  AE: "United Arab Emirates",
  HK: "Hong Kong",
  SG: "Singapore",
};

const isLedgerCountry = (value: string | null): value is LedgerCountry =>
  value === "AE" || value === "HK" || value === "SG";

const COMPARISON_OPTIONS: {
  value: LedgerBalanceComparisonOp;
  label: string;
}[] = [
  { value: "eq", label: "=" },
  { value: "lt", label: "<" },
  { value: "gt", label: ">" },
];

const getCurrencyOptions = (
  region?: string | null
): LedgerBalanceCurrency[] => {
  switch (region) {
    case "HK":
      return ["HKD", "USD"];
    case "AE":
      return ["AED", "USD"];
    default:
      return ["SGD", "USD"];
  }
};

const toMinorUnits = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const [whole, fraction = ""] = trimmed.split(".");
  return (
    BigInt(whole) * BigInt(100) +
    BigInt(fraction.padEnd(2, "0"))
  ).toString();
};

type AmountFilter = {
  op: LedgerBalanceComparisonOp | "";
  amount: string;
};

const emptyAmount = (): AmountFilter => ({ op: "", amount: "" });

const cursorFor = (
  item: LedgerBalanceResult["items"][number],
  sortBy: LedgerBalanceSortField
): LedgerBalanceCursor => ({
  accountId: item.accountId,
  sortValue:
    sortBy === "totalBalance"
      ? item.totalBalance
      : sortBy === "availableBalance"
      ? item.availableBalance
      : item.clientCreatedAt,
});

const LedgerBalances = () => {
  const { selectedRegion } = useRegion();
  const { assignedRoles, assignedCountries } = useAuth();
  // The header region control is hidden while admin authorization is off,
  // which is every deployed stage, so selectedRegion stays null. This page
  // still needs a country, and defaults to AE to match the AED currency.
  const headerCountry = isLedgerCountry(selectedRegion) ? selectedRegion : null;
  // AE isn't part of the APP_REGIONS/country entitlement model yet, so it
  // stays available to everyone who can reach this (already role-gated) page.
  // HK and SG mirror RegionSelector's entitlement check so a customer support
  // admin can't use this dropdown to see a country they aren't assigned to.
  const accessibleCountries = useMemo(
    () =>
      assignedRoles?.includes(USER_ROLES.SUPER_ADMIN)
        ? LEDGER_COUNTRIES
        : LEDGER_COUNTRIES.filter(
            (option) => option === "AE" || assignedCountries?.includes(option)
          ),
    [assignedCountries, assignedRoles]
  );
  const [country, setCountry] = useState<LedgerCountry>(headerCountry ?? "AE");
  const [followedHeader, setFollowedHeader] = useState(headerCountry);
  if (headerCountry !== followedHeader) {
    setFollowedHeader(headerCountry);
    if (headerCountry) setCountry(headerCountry);
  }
  if (!accessibleCountries.includes(country)) {
    setCountry("AE");
  }
  const currencyOptions = useMemo(() => getCurrencyOptions(country), [country]);
  const [currency, setCurrency] = useState<LedgerBalanceCurrency>("AED");
  const [sortBy, setSortBy] =
    useState<LedgerBalanceSortField>("clientCreatedAt");
  const [sortDir, setSortDir] = useState<LedgerBalanceSortDirection>("desc");
  const [totalInput, setTotalInput] = useState<AmountFilter>(emptyAmount);
  const [availableInput, setAvailableInput] =
    useState<AmountFilter>(emptyAmount);
  const [appliedTotal, setAppliedTotal] = useState<AmountFilter>(emptyAmount);
  const [appliedAvailable, setAppliedAvailable] =
    useState<AmountFilter>(emptyAmount);
  const [amountError, setAmountError] = useState<string>();
  const [page, setPage] = useState(1);
  const [cursors, setCursors] = useState<(LedgerBalanceCursor | undefined)[]>([
    undefined,
  ]);

  useEffect(() => {
    if (currencyOptions.length > 0 && !currencyOptions.includes(currency)) {
      setCurrency(currencyOptions[0]);
    }
  }, [currency, currencyOptions]);

  const resetPage = useCallback(() => {
    setPage(1);
    setCursors([undefined]);
  }, []);

  // Reset pagination during render (not in an effect) so the very render
  // that changes region/currency/sort already uses page 1 and no cursor,
  // instead of sending a stale cursor one render late.
  const resetKey = `${country}|${currency}|${sortBy}|${sortDir}`;
  const [committedResetKey, setCommittedResetKey] = useState(resetKey);
  if (resetKey !== committedResetKey) {
    setCommittedResetKey(resetKey);
    resetPage();
  }

  const applyAmounts = useCallback(() => {
    const totalMinor = toMinorUnits(totalInput.amount);
    const availableMinor = toMinorUnits(availableInput.amount);
    if (totalMinor === null || availableMinor === null) {
      setAmountError("Enter an amount with up to 2 decimal places.");
      return;
    }
    if (totalMinor && !totalInput.op) {
      setAmountError("Choose =, <, or > for the total balance.");
      return;
    }
    if (availableMinor && !availableInput.op) {
      setAmountError("Choose =, <, or > for the available balance.");
      return;
    }
    setAmountError(undefined);
    setAppliedTotal(
      totalMinor ? { op: totalInput.op, amount: totalMinor } : emptyAmount()
    );
    setAppliedAvailable(
      availableMinor
        ? { op: availableInput.op, amount: availableMinor }
        : emptyAmount()
    );
    resetPage();
  }, [availableInput, resetPage, totalInput]);

  const clearAmounts = useCallback(() => {
    setTotalInput(emptyAmount());
    setAvailableInput(emptyAmount());
    setAppliedTotal(emptyAmount());
    setAppliedAvailable(emptyAmount());
    setAmountError(undefined);
    resetPage();
  }, [resetPage]);

  const onSort = useCallback(
    (field: LedgerBalanceSortField) => {
      if (sortBy === field) {
        setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      } else {
        setSortBy(field);
        setSortDir("desc");
      }
    },
    [sortBy]
  );

  const searchQuery = useMemo<LedgerBalanceSearchQuery | undefined>(() => {
    if (!currencyOptions.includes(currency)) return undefined;
    const query: LedgerBalanceSearchQuery = {
      country,
      currency,
      sortBy,
      sortDir,
    };
    if (appliedTotal.op && appliedTotal.amount) {
      query.totalOp = appliedTotal.op;
      query.totalAmount = appliedTotal.amount;
    }
    if (appliedAvailable.op && appliedAvailable.amount) {
      query.availableOp = appliedAvailable.op;
      query.availableAmount = appliedAvailable.amount;
    }
    return query;
  }, [
    appliedAvailable,
    appliedTotal,
    currency,
    currencyOptions,
    country,
    sortBy,
    sortDir,
  ]);

  const cursor = cursors[page - 1];
  const { isLoading, error, data } = useQuery<LedgerBalanceResult, Error>(
    ["ledgerBalances", page, searchQuery, cursor],
    () =>
      getLedgerBalances({
        pageSize: PAGE_SIZE,
        searchQuery: searchQuery!,
        cursor,
      }),
    { enabled: Boolean(searchQuery) && (page === 1 || Boolean(cursor)) }
  );

  useEffect(() => {
    if (!data?.hasMore || data.items.length === 0) return;
    const next = cursorFor(data.items[data.items.length - 1], sortBy);
    setCursors((current) => {
      const existing = current[page];
      if (
        existing?.accountId === next.accountId &&
        existing.sortValue === next.sortValue
      ) {
        return current;
      }
      const updated = current.slice();
      updated[page] = next;
      return updated;
    });
  }, [data, page, sortBy]);

  const goToPage = (next: number) => {
    if (next < 1) return;
    if (next > 1 && !cursors[next - 1]) return;
    setPage(next);
  };

  const pageCount = data?.hasMore ? page + 1 : Math.max(page, 1);
  const totalItems = (page - 1) * PAGE_SIZE + (data?.items.length ?? 0);
  const hasAmountFilter = Boolean(
    appliedTotal.amount || appliedAvailable.amount
  );

  return (
    <div>
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Ledger balances</h1>
      </div>
      <Col>
        <div className={styles.filterRow}>
          <Dropdown className="add-filters-dropdown m-0">
            <Dropdown.Toggle
              variant="primary"
              className="search-toggle"
              data-testid="ledger-balances-country-filter"
            >
              <span>{COUNTRY_LABELS[country]}</span>
              <span className="material-icons material-icons-outlined">
                chevron_right
              </span>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {accessibleCountries.map((option) => (
                <Dropdown.Item
                  key={option}
                  data-testid={`ledger-balances-country-${option}`}
                  onClick={() => setCountry(option)}
                >
                  {COUNTRY_LABELS[option]}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <Dropdown className="add-filters-dropdown m-0">
            <Dropdown.Toggle
              variant="primary"
              className="search-toggle"
              data-testid="ledger-balances-currency-filter"
            >
              <span>{currency}</span>
              <span className="material-icons material-icons-outlined">
                chevron_right
              </span>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {currencyOptions.map((option) => (
                <Dropdown.Item key={option} onClick={() => setCurrency(option)}>
                  {option}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <label className={styles.amountFilter}>
            Total
            <select
              className="form-select"
              aria-label="Total balance comparison"
              data-testid="ledger-balances-total-op"
              value={totalInput.op}
              onChange={(event) =>
                setTotalInput((current) => ({
                  ...current,
                  op: event.target.value as AmountFilter["op"],
                }))
              }
            >
              <option value="">Any</option>
              {COMPARISON_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <input
              className={`form-control ${styles.amountInput}`}
              data-testid="ledger-balances-total-amount"
              inputMode="decimal"
              placeholder="0.00"
              value={totalInput.amount}
              onChange={(event) =>
                setTotalInput((current) => ({
                  ...current,
                  amount: event.target.value,
                }))
              }
              onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                if (event.key === "Enter") applyAmounts();
              }}
            />
          </label>
          <label className={styles.amountFilter}>
            Available
            <select
              className="form-select"
              aria-label="Available balance comparison"
              data-testid="ledger-balances-available-op"
              value={availableInput.op}
              onChange={(event) =>
                setAvailableInput((current) => ({
                  ...current,
                  op: event.target.value as AmountFilter["op"],
                }))
              }
            >
              <option value="">Any</option>
              {COMPARISON_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <input
              className={`form-control ${styles.amountInput}`}
              data-testid="ledger-balances-available-amount"
              inputMode="decimal"
              placeholder="0.00"
              value={availableInput.amount}
              onChange={(event) =>
                setAvailableInput((current) => ({
                  ...current,
                  amount: event.target.value,
                }))
              }
              onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                if (event.key === "Enter") applyAmounts();
              }}
            />
          </label>
          <Button
            className="search-btn"
            data-testid="ledger-balances-search"
            variant="success"
            onClick={applyAmounts}
          >
            Search
          </Button>
          {hasAmountFilter && (
            <Button
              variant="link"
              data-testid="ledger-balances-clear"
              onClick={clearAmounts}
            >
              Clear amount filters
            </Button>
          )}
        </div>
        {amountError && (
          <div
            className={styles.amountError}
            data-testid="ledger-balances-amount-error"
          >
            {amountError}
          </div>
        )}
        {data && (
          <div className="mt-3" data-testid="ledger-balances-result-count">
            {data.items.length} {data.items.length === 1 ? "result" : "results"}{" "}
            on this page
          </div>
        )}
      </Col>
      <div className={styles.container}>
        {data && (
          <PaginationComponent
            pageCount={pageCount}
            currentPage={page}
            itemOfPage={data.items.length}
            totalItems={totalItems}
            containerClass={styles.tableTopPagination}
            setCurrentPage={goToPage}
          />
        )}
        <LedgerBalancesTable
          data={data}
          error={error as Error | null}
          isLoading={isLoading}
          idleMessage={searchQuery ? undefined : "Loading ledger balances..."}
          country={country}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={onSort}
        />
        {data && (
          <PaginationComponent
            pageCount={pageCount}
            currentPage={page}
            itemOfPage={data.items.length}
            totalItems={totalItems}
            setCurrentPage={goToPage}
          />
        )}
      </div>
    </div>
  );
};

export default memo(LedgerBalances);
